import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Button from '../../../components/ui/Button'
import Icon from '../../../components/AppIcon'
import ProductSearchList from './ProductSearchList'
import ProductEditPanel from './ProductEditPanel'
import { FRAGRANCE_FIELDS, isFragrance } from '../utils/fragranceDraft.mjs'

const TAG_PRINT_INFO_MAX = 24

function normalizeText(value) {
  return String(value || '').trim()
}

function normalizeTags(value) {
  if (Array.isArray(value)) {
    return value.map((tag) => normalizeText(tag)).filter(Boolean).join(', ')
  }
  return String(value || '').split(',').map((tag) => tag.trim()).filter(Boolean).join(', ')
}

function normalizeWeight(value) {
  const raw = normalizeText(value)
  if (!raw) return ''
  const parsed = Number(raw)
  if (!Number.isFinite(parsed)) return raw
  return parsed % 1 === 0 ? String(parsed) : parsed.toFixed(2)
}

function normalizeTagPrintInfo(value) {
  return normalizeText(value).replace(/\s+/g, ' ').toUpperCase().slice(0, TAG_PRINT_INFO_MAX)
}

function truncatePreview(value, limit = 140) {
  const text = normalizeText(value)
  if (!text) return ''
  return text.length > limit ? `${text.slice(0, limit - 1)}…` : text
}

function buildSuggestionInput(product) {
  const variant = product?.variants?.[0] || {}
  return {
    imageBase64: product?.image || '',
    title: product?.title || '',
    description: product?.bodyHtml || product?.description || '',
    productType: product?.productType || '',
    category: product?.category || '',
    metafields: product?.metafields || {},
    tags: product?.tags || '',
    weight: variant?.weight || product?.weight || product?.metafields?.weight || ''
  }
}

function buildSuggestionCacheKey(product) {
  const variant = product?.variants?.[0] || {}
  return [
    product?.id || '',
    product?.updatedAt || '',
    product?.priceUpdatedAt || '',
    variant?.sku || '',
    variant?.barcode || '',
    variant?.price || '',
    variant?.weight || '',
    product?.tags || '',
    JSON.stringify(product?.metafields || {})
  ].join('|')
}

function buildSuggestionItems(aiResult, selectedProduct) {
  if (!aiResult || !selectedProduct) return []
  const variant = selectedProduct?.variants?.[0] || {}
  const currentMetafields = selectedProduct?.metafields || {}
  const current = {
    title: normalizeText(selectedProduct?.title),
    description: normalizeText(selectedProduct?.bodyHtml || selectedProduct?.description || ''),
    tags: normalizeTags(selectedProduct?.tags),
    weight: normalizeWeight(variant?.weight || selectedProduct?.weight || currentMetafields?.weight || ''),
    karat: normalizeText(currentMetafields?.karat),
    stoneType: normalizeText(currentMetafields?.stone_type),
    stoneCarat: normalizeText(currentMetafields?.stone_carat),
    tagPrintInfo: normalizeTagPrintInfo(currentMetafields?.tag_print_info)
  }

  const suggestions = []
  const pushDirect = (id, label, nextValue, preview) => {
    const value = normalizeText(nextValue)
    if (!value) return
    if (id === 'title' && value === current.title) return
    if (id === 'description' && value === current.description) return
    if (id === 'tags' && normalizeTags(value) === current.tags) return
    if (id === 'weight' && normalizeWeight(value) === current.weight) return
    suggestions.push({
      id,
      label,
      preview: preview || truncatePreview(value),
      patch: { direct: { [id]: id === 'tags' ? normalizeTags(value) : value } }
    })
  }

  const pushMetafield = (id, key, label, nextValue, preview, normalize = normalizeText, currentValue = '') => {
    const value = normalize(nextValue)
    if (!value) return
    if (value === currentValue) return
    suggestions.push({
      id,
      label,
      preview: preview || truncatePreview(value),
      patch: { metafields: { [key]: value } }
    })
  }

  pushDirect('title', 'Title', aiResult?.title)
  pushDirect('description', 'Description', aiResult?.description, truncatePreview(aiResult?.description, 180))
  pushDirect('tags', 'Tags', normalizeTags(aiResult?.tags))
  pushDirect('weight', 'Weight (g)', normalizeWeight(aiResult?.weight || aiResult?.metafields?.weight))

  if (isFragrance(selectedProduct)) {
    for (const [key, label] of Object.entries(FRAGRANCE_FIELDS)) {
      pushMetafield(key, key, label, aiResult?.metafields?.[key], '', normalizeText, normalizeText(currentMetafields[key]))
    }
  } else {
  pushMetafield(
    'karat',
    'karat',
    'Karat',
    aiResult?.metafields?.karat,
    truncatePreview(aiResult?.metafields?.karat, 80),
    normalizeText,
    current.karat
  )
  pushMetafield(
    'stone_type',
    'stone_type',
    'Stone Type',
    aiResult?.metafields?.stone_type,
    truncatePreview(aiResult?.metafields?.stone_type, 80),
    normalizeText,
    current.stoneType
  )
  pushMetafield(
    'stone_carat',
    'stone_carat',
    'Stone Carat',
    aiResult?.metafields?.stone_carat,
    truncatePreview(aiResult?.metafields?.stone_carat, 80),
    normalizeText,
    current.stoneCarat
  )
  }
  pushMetafield(
    'tag_print_info',
    'tag_print_info',
    'Tag Print Info',
    aiResult?.tagPrintInfo || aiResult?.metafields?.tag_print_info,
    truncatePreview(normalizeTagPrintInfo(aiResult?.tagPrintInfo || aiResult?.metafields?.tag_print_info), 80),
    normalizeTagPrintInfo,
    current.tagPrintInfo
  )

  return suggestions
}

function mergeSuggestionPatches(suggestions = []) {
  const merged = { direct: {}, metafields: {} }
  for (const item of suggestions) {
    if (item?.patch?.direct) {
      Object.assign(merged.direct, item.patch.direct)
    }
    if (item?.patch?.metafields) {
      Object.assign(merged.metafields, item.patch.metafields)
    }
  }
  return merged
}

const UpdateTab = ({
  shopifyProducts,
  shopifyLoading,
  shopifyError,
  shopifyLastSync,
  searchShopifyProducts,
  loadMoreShopify,
  shopifyHasMore,
  syncShopifyMore,
  shopifySyncing,
  updateShopifyPrice,
  ensureShopifyBarcode,
  updateShopifyInventory,
  updateProductFields,
  updateShopifyImage,
  regenerateFields,
  generateUpdateSuggestions,
  tagConfig,
  onPrintTag,
  compactViewport = false
}) => {
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [applySuggestionAction, setApplySuggestionAction] = useState(null)
  const [aiStatus, setAiStatus] = useState('idle')
  const [aiSuggestions, setAiSuggestions] = useState([])
  const [aiSummary, setAiSummary] = useState('')
  const [aiError, setAiError] = useState('')
  const suggestionCacheRef = useRef(new Map())
  const analysisTimerRef = useRef(null)
  const analysisRequestRef = useRef(0)

  const showTipsPanel = !compactViewport

  const selectedSummary = useMemo(() => {
    if (!selectedProduct) return null
    const variant = selectedProduct?.variants?.[0] || {}
    return {
      title: selectedProduct?.title || 'Untitled product',
      sku: variant?.sku || selectedProduct?.sku || '',
      type: selectedProduct?.productType || 'Jewelry'
    }
  }, [selectedProduct])

  const clearAnalysisTimer = useCallback(() => {
    if (analysisTimerRef.current) {
      clearTimeout(analysisTimerRef.current)
      analysisTimerRef.current = null
    }
  }, [])

  const triggerAiSuggestions = useCallback((product, { force = false, delayMs = 1000 } = {}) => {
    clearAnalysisTimer()
    if (!product) {
      setAiStatus('idle')
      setAiSuggestions([])
      setAiSummary('')
      setAiError('')
      return
    }

    if (!generateUpdateSuggestions) {
      setAiStatus('error')
      setAiSuggestions([])
      setAiSummary('')
      setAiError('AI suggestions are unavailable right now.')
      return
    }

    const cacheKey = buildSuggestionCacheKey(product)
    const cached = suggestionCacheRef.current.get(cacheKey)
    if (cached && !force) {
      setAiStatus('ready')
      setAiSuggestions(cached.suggestions || [])
      setAiSummary(cached.summary || '')
      setAiError('')
      return
    }

    setAiStatus(delayMs > 0 ? 'waiting' : 'loading')
    setAiSuggestions([])
    setAiSummary('')
    setAiError('')

    const requestId = ++analysisRequestRef.current
    analysisTimerRef.current = setTimeout(async () => {
      if (requestId !== analysisRequestRef.current) return
      setAiStatus('loading')
      try {
        const input = buildSuggestionInput(product)
        const aiResult = await generateUpdateSuggestions(input)
        const suggestions = buildSuggestionItems(aiResult, product)
        const summary = normalizeText(aiResult?.summary)
        const payload = { suggestions, summary }
        suggestionCacheRef.current.set(cacheKey, payload)
        if (requestId !== analysisRequestRef.current) return
        setAiStatus('ready')
        setAiSuggestions(suggestions)
        setAiSummary(summary)
        setAiError('')
      } catch (err) {
        if (requestId !== analysisRequestRef.current) return
        setAiStatus('error')
        setAiSuggestions([])
        setAiSummary('')
        setAiError(err?.message || 'Failed to get AI suggestions')
      }
    }, Math.max(0, delayMs))
  }, [clearAnalysisTimer, generateUpdateSuggestions])

  useEffect(() => {
    if (!selectedProduct) return
    triggerAiSuggestions(selectedProduct, { force: false, delayMs: 1000 })
  }, [selectedProduct, triggerAiSuggestions])

  useEffect(() => () => clearAnalysisTimer(), [clearAnalysisTimer])

  const layoutClass = showTipsPanel
    ? 'grid grid-cols-1 lg:grid-cols-[minmax(300px,2fr)_minmax(400px,3fr)] xl:grid-cols-[minmax(280px,1.7fr)_minmax(380px,2.3fr)_minmax(260px,1.3fr)] gap-4'
    : 'grid grid-cols-1 lg:grid-cols-[minmax(300px,2fr)_minmax(400px,3fr)] gap-4'

  const applyPatch = useCallback((patch) => {
    const hasDirect = patch?.direct && Object.keys(patch.direct).length > 0
    const hasMetafields = patch?.metafields && Object.keys(patch.metafields).length > 0
    if (!hasDirect && !hasMetafields) return
    setApplySuggestionAction({
      requestId: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      patch
    })
  }, [])

  const handleApplyAll = useCallback(() => {
    applyPatch(mergeSuggestionPatches(aiSuggestions))
  }, [aiSuggestions, applyPatch])

  const handleUpdateImage = useCallback(async (productId, imageDataUrl, fileName) => {
    if (!updateShopifyImage) {
      throw new Error('Image update is unavailable right now.')
    }
    const result = await updateShopifyImage(productId, imageDataUrl, fileName)
    if (result?.imageUrl) {
      setSelectedProduct((prev) => {
        if (!prev || prev.id !== productId) return prev
        return {
          ...prev,
          image: result.imageUrl,
          imageAlt: result.imageAlt || prev.imageAlt || null,
          updatedAt: new Date().toISOString()
        }
      })
    }
    return result
  }, [updateShopifyImage])

  return (
    <div className={layoutClass}>
      <ProductSearchList
        products={shopifyProducts}
        loading={shopifyLoading}
        error={shopifyError}
        lastSync={shopifyLastSync}
        onSearch={searchShopifyProducts}
        onLoadMore={loadMoreShopify}
        hasMore={shopifyHasMore}
        onSyncMore={syncShopifyMore}
        syncing={shopifySyncing}
        selectedId={selectedProduct?.id}
        onSelect={setSelectedProduct}
      />

      {selectedProduct ? (
        <ProductEditPanel
          product={selectedProduct}
          onUpdatePrice={updateShopifyPrice}
          onEnsureBarcode={ensureShopifyBarcode}
          onUpdateInventory={updateShopifyInventory}
          onUpdateFields={updateProductFields}
          onUpdateImage={handleUpdateImage}
          onRegenerateAI={regenerateFields}
          applySuggestionAction={applySuggestionAction}
          tagConfig={tagConfig}
          onPrintTag={onPrintTag}
          onClose={() => setSelectedProduct(null)}
        />
      ) : (
        <div className="bg-card rounded-xl border border-border/60 shadow-elevation-1 flex flex-col items-center justify-center p-8 min-h-[400px]">
          <Icon name="MousePointerClick" size={40} className="text-muted-foreground/40 mb-3" />
          <p className="text-sm font-medium text-muted-foreground">Select a product to edit</p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            Search and click on a product from the list
          </p>
        </div>
      )}

      {showTipsPanel && (
        <aside className="bg-card rounded-xl border border-border/60 shadow-elevation-1 p-4">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <Icon name="Sparkles" size={16} className="text-primary" />
              <h3 className="text-sm font-semibold text-foreground">AI Suggestions</h3>
            </div>
            {selectedProduct && (
              <button
                type="button"
                onClick={() => triggerAiSuggestions(selectedProduct, { force: true, delayMs: 0 })}
                className="text-xs text-primary hover:underline"
              >
                Refresh
              </button>
            )}
          </div>

          {!selectedProduct ? (
            <div className="space-y-2 text-xs text-muted-foreground">
              <p>Select a product. If it stays selected for 1 second, AI will generate suggestions.</p>
              <p>Use Apply Suggestion or Apply All to fill fields quickly.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-lg border border-border/60 bg-muted/20 p-3">
                <p className="text-xs text-muted-foreground mb-1">Selected</p>
                <p className="text-sm font-medium text-foreground truncate">{selectedSummary?.title}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {selectedSummary?.sku || 'No SKU'} • {selectedSummary?.type}
                </p>
              </div>

              {aiStatus === 'waiting' && (
                <div className="rounded-lg border border-border/60 bg-muted/10 p-3 text-xs text-muted-foreground">
                  Holding selection for 1 second before sending to AI...
                </div>
              )}

              {aiStatus === 'loading' && (
                <div className="rounded-lg border border-border/60 bg-muted/10 p-3 flex items-center gap-2 text-xs text-muted-foreground">
                  <Icon name="Loader2" size={14} className="animate-spin" />
                  Generating suggestions...
                </div>
              )}

              {aiStatus === 'error' && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3">
                  <p className="text-xs font-medium text-destructive">AI suggestion failed</p>
                  <p className="text-xs text-muted-foreground mt-1">{aiError || 'Try again.'}</p>
                </div>
              )}

              {aiStatus === 'ready' && aiSummary && (
                <div className="rounded-lg border border-primary/20 bg-primary/10 p-3">
                  <p className="text-xs font-medium text-primary">AI Summary</p>
                  <p className="text-xs text-muted-foreground mt-1">{aiSummary}</p>
                </div>
              )}

              {aiStatus === 'ready' && aiSuggestions.length > 0 && (
                <>
                  <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                    {aiSuggestions.map((item) => (
                      <div key={item.id} className="rounded-lg border border-border/60 p-2.5 bg-muted/10">
                        <p className="text-xs font-semibold text-foreground">{item.label}</p>
                        <p className="text-xs text-muted-foreground mt-1 break-words">{item.preview}</p>
                        <div className="mt-2">
                          <Button
                            size="xs"
                            variant="outline"
                            onClick={() => applyPatch(item.patch)}
                          >
                            Apply Suggestion
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleApplyAll}
                    className="w-full"
                  >
                    <Icon name="Sparkles" size={14} className="mr-1" />
                    Apply All
                  </Button>
                </>
              )}

              {aiStatus === 'ready' && aiSuggestions.length === 0 && (
                <div className="rounded-lg border border-border/60 bg-muted/10 p-3">
                  <p className="text-xs text-muted-foreground">
                    No new suggestions right now. Try Refresh after editing title/description/weight.
                  </p>
                </div>
              )}
            </div>
          )}
        </aside>
      )}
    </div>
  )
}

export default UpdateTab
